import { useEffect, useMemo } from 'react';

interface ScriptAttributes {
  [key: string]: string | boolean | undefined;
}

export interface ScriptConfig {
  src?: string;
  inlineContent?: string;
  isEnabled: boolean;
  async?: boolean;
  defer?: boolean;
  variables?: { [key: string]: string | null };
  attributes?: ScriptAttributes;
  type?: string;
}

export interface ScriptsConfig {
  isEnabled: boolean;
  scripts: ScriptConfig[];
}

const escapeScriptContent = (content: string): string => {
  return content
    .replace(/<\/script>/g, '<\\/script>')
    .replace(/<\//g, '<\\/')
    .replace(/\r?\n|\r/g, '');
};

const replaceVars = (content: string, variables: { [key: string]: string | null } = {}): string => {
  if (!content) return content;
  Object.keys(variables).forEach((key) => {
    const regex = new RegExp(`\\{${key}\\}`, 'g');
    content = content.replace(regex, variables[key] || '');
  });
  return escapeScriptContent(content);
};

const useInjectScripts = (scriptsConfig: ScriptsConfig) => {
  const processedScriptsConfig = useMemo(() => {
    return scriptsConfig;
  }, [scriptsConfig]);

  const renderScripts = () => {
    if (!processedScriptsConfig?.isEnabled) return null;

    return processedScriptsConfig.scripts
      .filter((script) => script.isEnabled && !script.inlineContent)
      .map((script, index) => {
        const src = replaceVars(script.src || '', script.variables);

        return <script key={index} src={src} type="text/javascript" async={script.async} defer={script.defer !== undefined ? script.defer : true} {...script.attributes} />;
      });
  };

  useEffect(() => {
    if (!processedScriptsConfig?.isEnabled) return;

    const injectedScripts: HTMLScriptElement[] = [];

    processedScriptsConfig.scripts
      .filter((script) => script.isEnabled && script.inlineContent)

      .forEach((script) => {
        const inlineContent = replaceVars(script.inlineContent || '', script.variables);
        const inlineScript = document.createElement('script');
        inlineScript.type = 'text/javascript';
        inlineScript.defer = script.defer !== undefined ? script.defer : true;
        inlineScript.async = script.async || false;
        inlineScript.textContent = inlineContent;

        if (script.attributes) {
          Object.entries(script.attributes).forEach(([key, value]) => {
            if (typeof value === 'boolean') {
              if (value) inlineScript.setAttribute(key, '');
            } else if (value) {
              inlineScript.setAttribute(key, value);
            }
          });
        }

        document.head.appendChild(inlineScript);
        injectedScripts.push(inlineScript);
      });

    return () => {
      injectedScripts.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [processedScriptsConfig]);

  return {
    renderScripts,
  };
};

export default useInjectScripts;
